import { InfoOutlined } from "@mui/icons-material";
import { Box, Switch, SwitchProps, SxProps, Theme, Typography, useTheme } from "@mui/material";
import React from "react";
import { Tooltip } from "./Tooltip";


export interface LabeledSwitchProps extends Omit<SwitchProps, "children"> {
  label: React.ReactNode;
  labelWidth?: number | string;
  spacing?: number;
  labelColor?: string;
  containerSx?: SxProps<Theme>;
  boxSx?: SxProps<Theme>;
  tooltip?: string;
  onChange?: SwitchProps["onChange"];
}

export const LabeledSwitch: React.FC<LabeledSwitchProps> = ({
  label,
  labelWidth = "136px",
  spacing = 2,
  labelColor,
  containerSx,
  boxSx,
  tooltip,
  onChange,
  ...switchProps
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: { xs: "flex-start", md: "center" },
        gap: { xs: "8px", md: spacing },
        height: { xs: "auto", md: 40 },
        width: "100%",
        minWidth: 0,
        ...containerSx,
      }}
    >
      <Typography
        component="div"
        sx={{
          minWidth: { xs: "auto", md: labelWidth },
          color: labelColor ?? (theme.colors.text[1] || theme.palette.text.primary),
          fontWeight: 700,
          fontSize: 12,
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          position: "relative",
          width: "fit-content",
          maxWidth: "100%",
          minWidth: 0,
          flex: 1,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1,
          ...boxSx,
        }}
      >
        <Switch onChange={onChange} {...switchProps} />
        {tooltip && (
          <Tooltip
            message={tooltip}
            placement="right"
          >
            <InfoOutlined
              data-testid="InfoOutlinedIcon"
              sx={{
                fontSize: 18,
                color: theme.colors.secondary[6],
                cursor: "pointer",
                ml: 1,
              }}
            />
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};
